import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import BlankImage from '../../../../assets/images/blank-image.png';
import { ProductCategoryConstant, ProductStatusConstant } from '../../../../constants/product/product.constant';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { IProduct } from '../../../../interfaces/Product.interfaces';
import {
  getGeneralMarketDetailPageURL,
  IProductForGetDetailPageURL,
} from '../../../../utils/getter/getProductDetailPageURL.util';
import { getGeneralProductPrice, IProductForGetProductPrice } from '../../../../utils/getter/getProductPrice.util';
import { LoginToCheckPrice } from '../components/LoginToCheckPrice';
import { ProductInstallmentInquiryButton } from '../components/ProductInstallmentInquiryButton';
import { ProductStatusBanner } from '../components/ProductStatusBanner';
import { ProductStatusText } from '../components/ProductStatusText';

export type IProductForOverviewCard = Pick<
  IProduct,
  | 'id'
  | 'machine'
  | 'code'
  | 'category'
  | 'images'
  | 'status'
  | 'title'
  | 'price'
  | 'priceVisible'
  | 'manufacturedYear'
  | 'operationTime'
>;

interface ProductOverviewProps {
  product: IProductForOverviewCard;
  pageType: 'home' | 'pdp' | 'mpdp';
  index?: number;
}

const { PLUVA, PARTNER } = ProductCategoryConstant;
const { PENDING, SOLDOUT, IN_CONTRACT, ONSALE } = ProductStatusConstant;

export const ProductOverviewCard = ({ product, pageType, index }: ProductOverviewProps) => {
  const navigate = useNavigate();

  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const price = useMemo<string>(() => {
    return getGeneralProductPrice({ product: product as IProductForGetProductPrice, isUserLoggedIn });
  }, [product]);

  const productDetailPageURL = useMemo<string>(() => {
    return getGeneralMarketDetailPageURL(product as IProductForGetDetailPageURL);
  }, [product.category]);

  return (
    <Container
      onClick={(e) => {
        e.stopPropagation();

        if (product.status !== SOLDOUT) {
          if (product.category === PLUVA) {
            ampli.홈플루바매물모아보기매물클릭({ '매물 코드': product.code });
          } else if (product.category === PARTNER) {
            ampli.홈파트너매물모아보기매물클릭({ '매물 코드': product.code });
          }

          if (pageType === 'mpdp') {
            ampli.pvmClickMpdpPremiumProduct({ title: product.title, sequence: index });
          }

          navigate(productDetailPageURL);
        }
      }}
    >
      <ImageWrap>
        {product.status === PENDING ? (
          <ProductStatusText text={'판매 예정'} />
        ) : (
          product.status === SOLDOUT && <ProductStatusText text={'판매 완료'} />
        )}

        <Image
          src={
            product.images.filter((image) => image.type === 'LIST').length > 0
              ? product.images.filter((image) => image.type === 'LIST')[0].imageUrl
              : BlankImage
          }
          alt={
            product.images.filter((image) => image.type === 'LIST').length > 0
              ? product.images.filter((image) => image.type === 'LIST')[0].imageUrl
              : BlankImage
          }
          isBlurred={product.status === PENDING || product.status === SOLDOUT}
          loading={'lazy'}
        />

        {(product.status === PENDING || product.status === IN_CONTRACT || product.status === SOLDOUT) && (
          <ProductStatusBanner productStatus={product.status} />
        )}
      </ImageWrap>

      <ProductInfo isBlurred={product.status === PENDING || product.status === SOLDOUT}>
        <Title>{product.title}</Title>

        <YearAndOprationTime>{`${String(product.manufacturedYear).slice(2, 4)}년식 · ${Number(
          product.operationTime,
        ).toLocaleString()}시간`}</YearAndOprationTime>

        <PriceTitleContainer>
          {isUserLoggedIn && <Price>{price}</Price>}
          {!isUserLoggedIn && <LoginToCheckPrice />}
        </PriceTitleContainer>
      </ProductInfo>

      {pageType === 'home' && <ProductInstallmentInquiryButton product={product} pageType={pageType} />}
    </Container>
  );
};

const Container = styled.div`
  min-width: 100%;
  overflow: hidden;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  gap: 8px;

  z-index: 1;

  &:hover {
    cursor: pointer;
  }
`;

const ImageWrap = styled.div`
  width: 100%;

  padding-top: 268px;

  position: relative;

  border-radius: 8px;

  background-color: #f2f3f5;
`;

const Image = styled.img<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  left: 0;
  top: 0;

  object-fit: cover;

  position: absolute;

  border-radius: 8px;

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};
`;

const ProductInfo = styled.div<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};

  gap: 6px;
`;

const Title = styled.h1`
  color: var(--light-scheme-monochrome-black, #232429);
  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 20.8px */

  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
`;

const YearAndOprationTime = styled.h2`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  /* Display/Small/s-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 15.6px */

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  align-self: stretch;

  overflow: hidden;
  text-overflow: ellipsis;
`;

const PriceTitleContainer = styled.div`
  width: 100%;

  display: flex;
  justify-content: flex-start;
  align-items: center;

  gap: 4px;
`;

const Price = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const DiscountWrap = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const Discount = styled.span`
  color: var(--light-scheme-color-primary-red, #ff3049);
  /* Display/Small/s-bold */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 15.6px */
`;
